import {
  DatePicker, type DatePickerProps, TimePicker, type TimePickerProps,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';

export interface DateTimeInputProps {
  // Parsable date string
  initialValue?: string;
  disabled?: boolean;
  onChange?: (value: Date) => void;
  dateInputProps?: DatePickerProps;
  timeInputProps?: TimePickerProps;
  withoutTime?: boolean;
  pastTimeNotAllowed?: boolean;
}

export default function DateTimeInput({
  initialValue,
  onChange,
  disabled,
  dateInputProps,
  timeInputProps,
  withoutTime,
  pastTimeNotAllowed = false,
}: DateTimeInputProps) {
  const currentValue = initialValue ? dayjs(initialValue) : null;

  const disabledTime = useMemo(() => {
    if (!pastTimeNotAllowed || !currentValue || !currentValue.isToday()) {
      return {};
    }

    const now = dayjs();
    const currentHour = now.hour();
    const currentMinute = now.minute();

    return {
      disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
      disabledMinutes: (hour: number) => (hour === currentHour
        ? Array.from({ length: currentMinute }, (_, i) => i)
        : []),
    };
  }, [pastTimeNotAllowed, currentValue]);

  const handleTimeChange = useCallback((time: Dayjs | null) => {
    if (!currentValue || !time || !onChange) return;

    const newValue = currentValue.hour(time.hour()).minute(time.minute()).second(time.second());
    onChange(newValue.toDate());
  }, [currentValue, onChange]);

  const handleDateChange = useCallback((date: Dayjs | null) => {
    if (!date || !onChange) return;

    let time = currentValue || dayjs().startOf('day');
    // When selecting a past time is not allowed, Set the time value to a future time.
    if (pastTimeNotAllowed && time.isBefore(dayjs())) {
      time = dayjs().add(1, 'hour');
    }
    const newValue = date
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second());

    onChange(newValue.toDate());
  }, [currentValue, onChange, pastTimeNotAllowed]);

  return (
    <>
      <DatePicker
        value={currentValue}
        onChange={handleDateChange}
        minDate={pastTimeNotAllowed ? dayjs() : undefined}
        {...dateInputProps}
        disabled={disabled}
      />
      {!withoutTime && (
        <TimePicker
          value={currentValue}
          onChange={handleTimeChange}
          minuteStep={15}
          disabled={disabled}
          use12Hours
          disabledTime={() => disabledTime}
          {...timeInputProps}
        />
      )}
    </>
  );
}
